import { useState, useCallback, useRef, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Search, Music, FileText, Users, Trophy, ArrowRight, X, TrendingUp,
  Flame, Star,
} from "lucide-react";

const TYPE_ICONS = {
  artist: Music,
  post: FileText,
  group: Users,
  challenge: Trophy,
};

const TYPE_LABELS = {
  artist: "Artiste",
  post: "Publication",
  group: "Groupe",
  challenge: "Defi",
};

const TYPE_COLORS = {
  artist: "#FF6B35",
  post: "#7000FF",
  group: "#FF8FA3",
  challenge: "#FFD700",
};

export default function SearchPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState(null);
  const [trending, setTrending] = useState(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    api.get("/trending").then((res) => setTrending(res.data)).catch(() => {});
  }, [api]);

  const doSearch = useCallback(
    async (q) => {
      if (!q.trim()) {
        setResults([]);
        return;
      }
      setLoading(true);
      try {
        const params = { q, limit: 30 };
        if (filterType) params.type = filterType;
        const res = await api.get("/search", { params });
        setResults(res.data.results);
      } catch (err) { console.error(err); }
      setLoading(false);
    },
    [api, filterType]
  );

  const handleInput = (val) => {
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  const showResults = query.trim().length > 0;

  return (
    <div className="py-6 space-y-5" data-testid="search-page">
      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <input
          ref={inputRef}
          placeholder="Rechercher artistes, posts, groupes, defis..."
          value={query}
          onChange={(e) => handleInput(e.target.value)}
          className="w-full bg-card border border-border rounded-2xl pl-12 pr-10 py-4 text-sm text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20 outline-none shadow-sm"
          data-testid="search-input"
        />
        {query && (
          <button onClick={() => { setQuery(""); setResults([]); }} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#FF6B35]">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Type filters */}
      <div className="flex gap-2">
        {[null, "artists", "posts", "groups", "challenges"].map((t) => (
          <button
            key={t || "all"}
            onClick={() => { setFilterType(t); if (query.trim()) doSearch(query); }}
            className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
              filterType === t
                ? "bg-[#FF6B35]/10 border-[#FF6B35]/40 text-[#FF6B35]"
                : "bg-foreground/5 border-foreground/10 text-muted-foreground"
            }`}
          >
            {t ? TYPE_LABELS[t.replace("s", "").replace("ie", "i")] || t : "Tous"}
          </button>
        ))}
      </div>

      {/* Results */}
      {showResults ? (
        <div className="space-y-2">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-12">
              <Search className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Aucun resultat pour "{query}"</p>
            </div>
          ) : (
            results.map((r) => {
              const Icon = TYPE_ICONS[r.type] || Star;
              const color = TYPE_COLORS[r.type] || "#8A8295";
              return (
                <button
                  key={`${r.type}-${r.id}`}
                  onClick={() => navigate(r.url)}
                  className="w-full bg-card border border-border rounded-2xl p-4 flex items-center gap-3 hover:border-[#FF6B35]/30 transition-colors text-left"
                  data-testid={`search-result-${r.type}-${r.id}`}
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}20` }}>
                    {r.avatar ? (
                      <img src={r.avatar} alt="" className="w-full h-full rounded-xl object-cover" />
                    ) : (
                      <Icon className="w-5 h-5" style={{ color }} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="font-heading font-bold text-sm text-card-foreground truncate">{r.title}</p>
                      {r.verified && (
                        <svg className="w-3.5 h-3.5 text-[#7000FF] shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{r.subtitle}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: `${color}15`, color }}>{TYPE_LABELS[r.type]}</span>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{r.meta}</p>
                  </div>
                </button>
              );
            })
          )}
        </div>
      ) : trending ? (
        <div className="space-y-5">
          {/* Trending artists */}
          {trending.top_artists?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Flame className="w-4 h-4 text-[#FF6B35]" />
                <h2 className="font-heading font-bold text-sm text-foreground">Artistes en vogue</h2>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                {trending.top_artists.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => navigate(`/artist/${a.id}`)}
                    className="flex flex-col items-center gap-2 min-w-[80px] shrink-0"
                    data-testid={`trending-artist-${a.id}`}
                  >
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FF8FA3] p-0.5">
                      <div className="w-full h-full rounded-full bg-background flex items-center justify-center overflow-hidden">
                        {a.avatar_url ? (
                          <img src={a.avatar_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-foreground font-bold text-sm">{a.name[0]}</span>
                        )}
                      </div>
                    </div>
                    <p className="text-[10px] text-foreground font-medium truncate w-full text-center">{a.name}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Active challenges */}
          {trending.active_challenges?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="w-4 h-4 text-[#FFD700]" />
                <h2 className="font-heading font-bold text-sm text-foreground">Defis populaires</h2>
              </div>
              <div className="space-y-2">
                {trending.active_challenges.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => navigate("/challenges")}
                    className="w-full bg-card border border-border rounded-2xl p-3.5 flex items-center gap-3 hover:border-[#FF6B35]/30 transition-colors text-left"
                    data-testid={`trending-challenge-${c.id}`}
                  >
                    <div className="w-9 h-9 rounded-xl bg-[#FFD700]/15 flex items-center justify-center shrink-0">
                      <Trophy className="w-4 h-4 text-[#FFD700]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-heading font-bold text-xs text-card-foreground truncate">{c.title}</p>
                      <p className="text-[10px] text-muted-foreground">par {c.artist_name}</p>
                    </div>
                    <span className="font-mono-credits font-bold text-xs text-[#FF6B35]">+{c.reward_credits}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Top posts */}
          {trending.top_posts?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-[#FF8FA3]" />
                <h2 className="font-heading font-bold text-sm text-foreground">Posts populaires</h2>
              </div>
              <div className="space-y-2">
                {trending.top_posts.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => navigate("/feed")}
                    className="w-full bg-card border border-border rounded-2xl p-3.5 flex items-center gap-3 hover:border-[#FF6B35]/30 transition-colors text-left"
                    data-testid={`trending-post-${p.id}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#7000FF] to-[#FF8FA3] flex items-center justify-center shrink-0">
                      {p.artist_avatar ? (
                        <img src={p.artist_avatar} alt="" className="w-full h-full rounded-full object-cover" />
                      ) : (
                        <span className="text-white text-[10px] font-bold">{(p.artist_name || "?")[0]}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-card-foreground truncate">{(p.content || "").slice(0, 60)}</p>
                      <p className="text-[10px] text-muted-foreground">{p.artist_name} &middot; {p.likes_count || 0} likes</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
